import { useCallback } from 'react';
import { chatClient } from '@/services/chat-client';
import { useChatStore } from '@/stores/chat-store';

/** Hook wrapping AI SSE streaming with per-conversation state management */
export function useAiStream(): {
  sendMessage: (conversationId: string, content: string) => Promise<void>;
} {
  const sendMessage = useCallback(
    async (conversationId: string, content: string) => {
      const store = useChatStore.getState();
      if (store.isAiStreaming) return;

      store.addUserMessage({
        id: crypto.randomUUID(),
        conversationId,
        role: 'user',
        content,
        createdAt: new Date().toISOString(),
      });

      store.startAiStream();

      let fullResponse = '';
      try {
        const stream = chatClient.streamAiConversation(conversationId, content);
        for await (const chunk of stream) {
          fullResponse += chunk;
          useChatStore.getState().appendAiChunk(chunk);
        }
        useChatStore.getState().finishAiStream({
          id: crypto.randomUUID(),
          conversationId,
          role: 'assistant',
          content: fullResponse,
          createdAt: new Date().toISOString(),
        });

        const conversations = useChatStore.getState().aiConversations;
        const conv = conversations.find((c) => c.id === conversationId);
        if (conv?.title === 'New conversation') {
          const updated = conversations.map((c) =>
            c.id === conversationId
              ? { ...c, title: content.length > 50 ? content.slice(0, 47) + '...' : content }
              : c,
          );
          useChatStore.setState({ aiConversations: updated });
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'AI request failed';
        useChatStore.getState().failAiStream(message);
      }
    },
    [],
  );

  return { sendMessage };
}
